import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { api } from "@/App";
import { toast } from "sonner";
import { Maximize2, Minimize2, X } from "lucide-react";
import { AdeLoadingSpinner } from "./AdeLoadingSpinner";
import { getSceneImage } from "./IslandScene";
import "leaflet/dist/leaflet.css";

/*
 * ADE World Map — Real satellite ocean tiles (ESRI World Imagery)
 * Looks like Google Earth. Islands are positioned in the middle of the Pacific Ocean
 * where there's nothing but water in reality. Always crisp at any zoom level.
 */

// ADE world at Point Nemo — the most remote spot in the ocean, nothing but water
const WORLD_CENTER = [-48, -123];
const MIN_ZOOM = 3;
const MAX_ZOOM = 10;
const DEFAULT_ZOOM = 5;

const ADE_BOUNDS = {
  latMin: -53, latMax: -43,
  lngMin: -128, lngMax: -118,
};

function adeToLatLng(bx, by, worldW = 10000, worldH = 7000) {
  const lat = ADE_BOUNDS.latMax - (by / worldH) * (ADE_BOUNDS.latMax - ADE_BOUNDS.latMin);
  const lng = ADE_BOUNDS.lngMin + (bx / worldW) * (ADE_BOUNDS.lngMax - ADE_BOUNDS.lngMin);
  return [lat, lng];
}

// Special large icon for the currently visited island
export function createVisitedIslandIcon(isPremium, zoom, islandType) {
  const imgSrc = getSceneImage(islandType, isPremium);
  const size = 90;
  return L.divIcon({
    className: "ade-island-marker",
    html: `<div style="width:${size}px;height:${size}px;position:relative;">
      <div style="position:absolute;inset:-6px;border-radius:50%;border:3px solid #0ea5e9;animation:visited-pulse 2s ease-out infinite;"></div>
      <img src="${imgSrc}" style="width:100%;height:100%;border-radius:50%;object-fit:cover;border:3px solid #0ea5e9;box-shadow:0 0 30px rgba(14,165,233,0.6), 0 4px 24px rgba(0,0,0,0.5);" draggable="false"/>
    </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

// Create custom island marker icons — progressive visibility based on zoom
function createIslandIcon(isOwn, isConnected, isPremium, zoom, islandType) {
  const color = isOwn ? "#ec4899" : isConnected ? "#10b981" : isPremium ? "#fbbf24" : "#ffffff";
  const borderColor = isOwn ? "#ec4899" : isConnected ? "#10b981" : isPremium ? "#fbbf24" : "rgba(255,255,255,0.7)";
  const imgSrc = getSceneImage(islandType, isPremium);
  
  // Zoom 9-10: large island circles with image — clearly visible on the ocean
  if (zoom >= 9) {
    const size = Math.min(40 + (zoom - 9) * 25, 80);
    return L.divIcon({
      className: "ade-island-marker",
      html: `<div style="width:${size}px;height:${size}px;position:relative;">
        <img src="${imgSrc}" style="width:100%;height:100%;border-radius:50%;object-fit:cover;border:3px solid ${borderColor};box-shadow:0 0 16px ${color}66, 0 3px 12px rgba(0,0,0,0.5);" draggable="false"/>
        ${isOwn ? '<div style="position:absolute;inset:-4px;border-radius:50%;border:2px solid #ec4899;animation:ade-pulse 2s infinite;"></div>' : ''}
      </div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  }

  // Zoom 8: medium circles with image — start to see what they are
  if (zoom >= 8) {
    const size = isOwn || isPremium ? 28 : isConnected ? 24 : 18;
    return L.divIcon({
      className: "ade-island-marker",
      html: `<div style="width:${size}px;height:${size}px;">
        <img src="${imgSrc}" style="width:100%;height:100%;border-radius:50%;object-fit:cover;border:2px solid ${borderColor};box-shadow:0 0 8px ${color}44, 0 2px 6px rgba(0,0,0,0.4);" draggable="false"/>
      </div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  }

  // Zoom 7: small colored dots
  if (zoom >= 7) {
    if (!isOwn && !isConnected && !isPremium) {
      const size = 5;
      return L.divIcon({
        className: "ade-island-marker",
        html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};opacity:0.4;"></div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
    }
    const size = isOwn ? 8 : 6;
    return L.divIcon({
      className: "ade-island-marker",
      html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:1.5px solid ${borderColor};box-shadow:0 0 6px ${color}55;${isOwn ? 'animation:ade-pulse 2s infinite;' : ''}"></div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  }

  // Below zoom 7: invisible
  return L.divIcon({
    className: "ade-island-marker",
    html: '<div style="width:0;height:0;"></div>',
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
}

// Component to track zoom level and update markers
function ZoomTracker({ setZoomLevel }) {
  const map = useMap();
  useEffect(() => {
    const onZoom = () => setZoomLevel(map.getZoom());
    map.on("zoomend", onZoom);
    setZoomLevel(map.getZoom());
    return () => map.off("zoomend", onZoom);
  }, [map, setZoomLevel]);
  return null;
}

// Component to fly to a location
function FlyTo({ position, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, zoom || 9, { duration: 1.5 });
    }
  }, [position, zoom, map]);
  return null;
}

export function AdeWorldMap({ onVisitIsland, currentUserId, flyToIsland, fill = false }) {
  const [islands, setIslands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myIsland, setMyIsland] = useState(null);
  const [worldSize, setWorldSize] = useState({ width: 10000, height: 7000 });
  // la mappa occupa tutto lo spazio disponibile fino alla barra inferiore
  const [mapHeight, setMapHeight] = useState(() =>
    typeof window !== "undefined" ? Math.max(300, window.innerHeight - 380) : 400
  );
  useEffect(() => {
    const onResize = () => setMapHeight(Math.max(300, window.innerHeight - 380));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM);
  const [flyTarget, setFlyTarget] = useState(null);
  const [flyZoom, setFlyZoom] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const mapRef = useRef(null);
  const savedViewRef = useRef(null);

  const toggleFullscreen = useCallback(() => {
    const m = mapRef.current;
    if (m) savedViewRef.current = { center: m.getCenter(), zoom: m.getZoom() };
    setIsFullscreen(prev => !prev);
    setTimeout(() => mapRef.current?.invalidateSize(), 80);
    setTimeout(() => mapRef.current?.invalidateSize(), 350);
  }, []);

  // blocca lo scroll della pagina in fullscreen
  useEffect(() => {
    if (isFullscreen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [isFullscreen]);

  const fetchIslands = useCallback(async () => {
    try {
      setLoading(true);
      const [res, myRes] = await Promise.all([
        api.get("/islands/world-map"),
        api.get("/islands/my/island"),
      ]);
      setIslands(res.data.islands || []);
      setMyIsland(myRes.data);
      if (res.data.world_size) setWorldSize(res.data.world_size);
    } catch (err) {
      console.error(err);
      toast.error("Errore nel caricamento della mappa");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchIslands(); }, [fetchIslands]);

  // Fly to visited island
  useEffect(() => {
    if (flyToIsland && flyToIsland.position_x && flyToIsland.position_y) {
      const pos = adeToLatLng(flyToIsland.position_x, flyToIsland.position_y, worldSize.width, worldSize.height);
      setFlyTarget(pos);
      setFlyZoom(10);
    }
  }, [flyToIsland, worldSize]);

  // Find my island position
  const myIslandPos = useMemo(() => {
    const me = islands.find(i => i.is_own);
    if (!me) return null;
    return adeToLatLng(me.position_x, me.position_y, worldSize.width, worldSize.height);
  }, [islands, worldSize]);

  // Initial map center: my island or world center
  const initialCenter = myIslandPos || WORLD_CENTER;

  const goToMyIsland = () => {
    if (myIslandPos) {
      setFlyTarget(myIslandPos);
      setFlyZoom(9);
    }
  };

  // in fullscreen la mappa (portale) coprirebbe l'animazione di viaggio: chiudi prima
  const handleVisit = (island) => {
    if (isFullscreen) {
      const m = mapRef.current;
      if (m) savedViewRef.current = { center: m.getCenter(), zoom: m.getZoom() };
      setIsFullscreen(false);
    }
    onVisitIsland(island);
  };

  if (loading) {
    return (
      <div className="relative rounded-2xl overflow-hidden" style={{ height: fill ? "100%" : mapHeight, background: "#04293a", zIndex: 0, isolation: "isolate" }}>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <AdeLoadingSpinner size="lg" />
            <p className="mt-4 font-medium text-sm animate-pulse">Caricamento Mondo ADE...</p>
          </div>
        </div>
      </div>
    );
  }

  const mapUi = (
    <div
      className={isFullscreen ? "fixed inset-0 overflow-hidden" : "relative rounded-2xl overflow-hidden shadow-2xl"}
      style={isFullscreen
        ? { zIndex: 4000, background: "#04293a", height: "100dvh", width: "100vw" }
        : { height: fill ? "100%" : mapHeight, zIndex: 0, isolation: "isolate" }}
      data-testid="ade-world-map"
    >
      <MapContainer
        key={isFullscreen ? "map-fs" : "map-inline"}
        ref={mapRef}
        center={savedViewRef.current?.center || initialCenter}
        zoom={savedViewRef.current?.zoom ?? DEFAULT_ZOOM}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        style={{ height: "100%", width: "100%", background: "#04293a" }}
        zoomControl={false}
        attributionControl={false}
      >
        {/* ESRI Satellite — real ocean imagery; .ade-ocean-tiles excludes dark-mode invert filter */}
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          maxZoom={10}
          tileSize={256}
          className="ade-ocean-tiles"
        />

        {/* Track zoom level */}
        <ZoomTracker setZoomLevel={setZoomLevel} />

        {/* Fly to target */}
        {flyTarget && <FlyTo position={flyTarget} zoom={flyZoom} />}

        {/* Island markers — all islands visible from zoom 7+ */}
        {zoomLevel >= 7 && islands.map(island => {
          const pos = adeToLatLng(island.position_x, island.position_y, worldSize.width, worldSize.height);
          const isVisited = flyToIsland?.user_id === island.user_id;
          const icon = isVisited
            ? createVisitedIslandIcon(island.is_premium, zoomLevel, island.island_type)
            : createIslandIcon(island.is_own, island.is_connected, island.is_premium, zoomLevel, island.island_type);

          return (
            <Marker
              key={island.user_id}
              position={pos}
              icon={icon}
              eventHandlers={{
                click: () => handleVisit(island),
              }}
            >
              {zoomLevel >= 9 && (
                <Popup className="ade-popup" closeButton={false} autoPan={false}>
                  <div className="text-center px-1 py-0.5 min-w-[120px]">
                    <p className="font-bold popup-title text-sm">{island.island_name}</p>
                    <p className="text-[10px] popup-sub">
                      {island.is_own ? "La tua isola" : island.is_connected ? island.owner_name : "Abitante sconosciuto"}
                    </p>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleVisit(island); }}
                      className="mt-1 px-3 py-1 text-[10px] font-semibold text-white rounded-full"
                      style={{ background: "linear-gradient(135deg, #0891b2, #0369a1)" }}
                    >
                      Visita
                    </button>
                  </div>
                </Popup>
              )}
            </Marker>
          );
        })}
      </MapContainer>

      {/* My island button */}
      {myIsland && (
        <button
          onClick={goToMyIsland}
          className="absolute z-[1000] bg-white/90 backdrop-blur-md border border-pink-400/40 rounded-full px-3 py-1.5 text-xs font-medium text-pink-600 hover:bg-pink-50 transition-all active:scale-95 shadow-lg"
          style={{ top: isFullscreen ? "calc(env(safe-area-inset-top, 0px) + 12px)" : 12, right: 12 }}
          data-testid="center-my-island-btn"
        >
          La mia isola
        </button>
      )}

      {/* Fullscreen toggle */}
      <button
        onClick={toggleFullscreen}
        className="absolute z-[1001] w-10 h-10 rounded-full flex items-center justify-center text-cyan-100 transition-all active:scale-90"
        style={{
          top: isFullscreen ? "calc(env(safe-area-inset-top, 0px) + 12px)" : 12,
          left: 12,
          background: "rgba(6, 20, 34, 0.72)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          border: "1px solid rgba(127,231,255,0.35)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.45), 0 0 18px rgba(127,231,255,0.18)",
        }}
        data-testid={isFullscreen ? "map-exit-fullscreen-btn" : "map-fullscreen-btn"}
        aria-label={isFullscreen ? "Esci da schermo intero" : "Schermo intero"}
      >
        {isFullscreen ? <Minimize2 className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} /> : <Maximize2 style={{ width: 18, height: 18 }} />}
      </button>

      {/* Pill di uscita ben visibile in basso */}
      {isFullscreen && (
        <button
          onClick={toggleFullscreen}
          className="absolute z-[1001] left-1/2 -translate-x-1/2 px-6 py-3 rounded-full text-[11px] font-extrabold tracking-[0.16em] uppercase text-white flex items-center gap-1.5 active:scale-95 transition-transform"
          style={{
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)",
            background: "rgba(6, 20, 34, 0.8)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            border: "1px solid rgba(127,231,255,0.4)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
          }}
          data-testid="map-exit-fullscreen-pill"
        >
          <X style={{ width: 14, height: 14 }} /> Chiudi mappa
        </button>
      )}

      {/* Custom CSS */}
      <style>{`
        .ade-island-marker {
          background: transparent !important;
          border: none !important;
        }
        @keyframes ade-pulse {
          0%, 100% { box-shadow: 0 0 6px #ec489966; transform: scale(1); }
          50% { box-shadow: 0 0 14px #ec489999; transform: scale(1.15); }
        }
        @keyframes visited-pulse {
          0% { transform: scale(1); opacity: 0.8; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        .ade-popup .leaflet-popup-content-wrapper {
          border-radius: 14px;
          padding: 4px;
          background: rgba(10, 16, 32, 0.82);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255,255,255,0.14);
          box-shadow: 0 8px 26px rgba(0,0,0,0.45);
        }
        .ade-popup .leaflet-popup-tip {
          background: rgba(10, 16, 32, 0.82);
        }
        .ade-popup .popup-title { color: #ffffff; }
        .ade-popup .popup-sub { color: rgba(255,255,255,0.65); }
        .leaflet-container {
          font-family: system-ui, sans-serif;
        }
      `}</style>
    </div>
  );

  return isFullscreen ? createPortal(mapUi, document.body) : mapUi;
}

export default AdeWorldMap;
